/**
 * Mini cart component
 * Displays cart items, quantities, pricing, discounts, promolines, and free shipping progress
 */

import { useEffect, useLayoutEffect, useState, useMemo } from 'preact/hooks'
import { formatMoney, globalState } from 'helpers/utils'
import { emitterProps, globalEmitter } from 'helpers/emitter-event'
import useCartGlobal, { CartConfigKeys } from 'hooks/useCartGlobal'

import { createPortal } from 'preact/compat'
import PreactImage from 'snippets/image/image'
import Icon from 'snippets/icon/icon'
import { usePromolines } from '@/components/hooks/usePromolines'
import { Promoline } from 'libs/searchspring/components/Promoline'
import { useGwp } from '@/components/hooks/useGwp'

const actionKeys = {
  SELECT: 'select',
  REMOVE: 'remove'
}

const QUANTITY_OPTIONS_MAX = 10

const getLineItemDisplayName = (item) =>
  item.customer_facing_product_name || item.product_title

/** Builds the quantity options for the select dropdown, capped at inventory. */
const buildQuantityOptions = (currentQuantity, inventoryQuantity) => {
  let max = QUANTITY_OPTIONS_MAX
  if (typeof inventoryQuantity === 'number' && inventoryQuantity > 0 && inventoryQuantity < max) {
    max = inventoryQuantity
  }
  if (currentQuantity > max) {
    max = currentQuantity
  }
  if (max < 1) max = 1
  return Array.from({ length: max }, (_, i) => i + 1)
}

const onChangeActionValue = (key, target) => {
  return {
    [actionKeys.SELECT]: parseInt(target.value, 10),
    [actionKeys.REMOVE]: 0
  }[key]
}

/**
 *
 * Handles line item quantity update.
 *
 * @param {*} param0
 * @returns
 */
const MiniCartItemQuantity = ({ item, handleSetCartConfig, itemLoading, setItemLoading }) => {
  const [error, setError] = useState(null)
  const [optimisticQuantity, setOptimisticQuantity] = useState(null)

  const quantityOptions = useMemo(
    () => buildQuantityOptions(item.quantity, item.inventory_quantity),
    [item.quantity, item.inventory_quantity]
  )

  useEffect(() => {
    setOptimisticQuantity(null)
  }, [item.quantity])

  const handleLineItemQuantityChange = (item, newQuantity) => {
    if (newQuantity > item.inventory_quantity) {
      setError('Maximum quantity reached')
      setTimeout(() => setError(null), 5000)
      throw new Error('Unavailable product stock quantity')
    }
    setError(null)
  }

  const applyQuantityChange = async (newQuantity) => {
    if (itemLoading) return
    try {
      handleLineItemQuantityChange(item, newQuantity)
    } catch (err) {
      globalEmitter.emit(emitterProps.notice.global, {
        type: 'error',
        content: err.message
      })
      return
    }
    if (newQuantity > 0) setOptimisticQuantity(newQuantity)
    setItemLoading(true)
    try {
      await window.Cart.updateLineItemQuantity(
        {
          id: item.variant_id,
          quantity: newQuantity
        },
        { silent: false }
      )
      await window.Cart.getCart()
      handleSetCartConfig(CartConfigKeys.cart, window.Cart.value)
    } catch (error) {
      setOptimisticQuantity(null)
      globalEmitter.emit(emitterProps.notice.global, {
        type: 'error',
        content: error.message
      })
    } finally {
      setItemLoading(false)
    }
  }

  const onLineItemQuantityChange = async (e) => {
    e.preventDefault()
    const action = e.target.dataset.action
    const newQuantity = onChangeActionValue(action, e.target)
    await applyQuantityChange(newQuantity)
  }

  const onRemoveClick = async (e) => {
    e.preventDefault()
    await applyQuantityChange(0)
  }

  return (
    <div className="grow flex items-end justify-between gap-x-0.5">
      <form onChange={onLineItemQuantityChange} className="shrink-0 flex flex-col">
        <label className="relative flex items-center w-fit h-[44px] py-0.25 cursor-pointer link-fade">
          <span className="absolute left-0 top-1/2 -translate-y-1/2 paragraph-xs whitespace-nowrap">Qty: </span>
          <select
            data-action={actionKeys.SELECT}
            value={optimisticQuantity ?? item.quantity}
            className="paragraph-xs leading-none h-full pl-1.625 pr-0.75 py-0 border-0 bg-none bg-transparent appearance-none cursor-pointer focus:outline-none focus:ring-0 focus:ring-offset-0 z-2"
            aria-label="Quantity"
          >
            {quantityOptions.map((qty) => (
              <option value={qty} key={qty}>{qty}</option>
            ))}
          </select>
          <span className="absolute right-0 top-1/2 -translate-y-1/2 w-[8px] h-[4px] pointer-events-none children:size-full">
            <Icon id="chevron-down" />
          </span>
        </label>
        <p
          className="empty:hidden w-fit max-h-0 pt-0.5 opacity-0 paragraph-xs text-type-error transition-all"
          data-quantity-error
        >
          {error}
        </p>
      </form>

      <div className="mb-0.25">
        <a
          href={item.url_to_remove || '#'}
          className="btn btn-text btn-tertiary btn-s w-fit"
          aria-label="Remove item"
          onClick={onRemoveClick}
        >
          <span className="text-line text-line-large">
            Remove
          </span>
        </a>
      </div>
    </div>
  )
}

/**
 *
 *
 * Mini Cart free shipping bar.
 *
 * Driven by the active market's shipping settings metaobjects
 * (market.metafields.custom.shipping_settings), exposed on
 * window.globals.metaobjects.shipping_settings and ordered by min_threshold.
 * Renders nothing when the market has no shipping settings assigned.
 *
 * @param {*} param0
 * @returns
 */
function MiniCartSnippet({ cartSubTotal }) {
  const shippingSettings = window.globals?.metaobjects?.shipping_settings ?? []
  if (!shippingSettings.length) return null

  const sortedSettings = [...shippingSettings].sort(
    (a, b) => Number(a.min_threshold) - Number(b.min_threshold)
  )
  const firstSetting = sortedSettings[0]
  // Highest threshold the cart has reached, or null when below the first.
  const achievedSetting = sortedSettings.reduce(
    (reached, setting) => (cartSubTotal >= Number(setting.min_threshold) ? setting : reached),
    null
  )

  const message = achievedSetting
    ? `You are receiving ${achievedSetting.title} on this order`
    : `Spend ${formatMoney(Number(firstSetting.min_threshold) - cartSubTotal)} more to unlock ${firstSetting.title}`

  const progressBarPercentage = achievedSetting
    ? 100
    : Math.round((cartSubTotal / Number(firstSetting.min_threshold)) * 100)
  const progressBarPercentageWidth = progressBarPercentage > 100 ? '100' : progressBarPercentage
  return (
    <div data-role="free_shipping_monitor" className="flex flex-col border-b border-borders-default">
      <div className="relative w-full h-[4px] bg-page-tertiary">
        <div
          className="absolute inset-tl w-shipping-bar-width h-full bg-brand-black transition-all"
          style={{ '--shipping-bar-width': progressBarPercentageWidth + '%' }}
        ></div>
      </div>
      <div className="flex px-1 md:px-1.5 py-0.875">
        <p className="paragraph-xs">{message}</p>
      </div>
    </div>
  )
}

const MiniCartGiftItem = ({ gift, handleSetCartConfig, isLoading }) => {
  const loadingState = isLoading ? 'opacity-50' : ''
  const onGwpAddToCart = async (e) => {
    try {
      e.preventDefault()
      const form = new FormData(e.target)
      const id = form.get('id')
      const quantity = form.get('quantity')
      const giftId = form.get('properties[_bl_gwp]')
      /** Set MiniCart state to isLoading true */
      handleSetCartConfig(CartConfigKeys.isLoading, true)
      /** Add line item to cart */
      await window.Cart.addLineItems(
        {
          id,
          quantity,
          properties: {
            _bl_gwp: giftId
          }
        },
        { silent: false }
      )

      /** Poll Shopify for new Cart item and set cart config state */
      await window.Cart.getCart()
      handleSetCartConfig(CartConfigKeys.cart, window.Cart.value)
      handleSetCartConfig(CartConfigKeys.isLoading, false)
    } catch (error) {
      globalEmitter.emit(emitterProps.notice.global, {
        type: 'error',
        content: error.message
      })
      handleSetCartConfig(CartConfigKeys.isLoading, false)
    }
  }

  if (!gift.customerQualifies) {
    return (
      <div className="flex flex-col gap-y-0.5 bg-type-white-secondary px-1 md:px-1.5 py-1.5">
        <div className="paragraph-l font-bold">Join the club</div>
        <div className="flex flex-col md:flex-row justify-between gap-0.5">
          <div className="paragraph-s">Join the club to get a {gift.giftName}!</div>
          <div className="paragraph-s font-bold flex flex-row gap-x-0.5">
            <a href={globalState.routes.account_register_url} className="link-fade">Join&nbsp;now</a>
            <a href={globalState.routes.account_login_url} className="link-fade">Login</a>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`flex flex-col gap-y-1 bg-type-white-secondary px-1 md:px-1.5 py-1.5 overflow-hidden flickity-nav-inline flickity-nav-transparent ${loadingState}`}>
      <div className="paragraph-s font-bold">You qualify for a {gift.giftName}!</div>
      <x-carousel
        className="flickity-container flex w-full h-fit !min-h-0"
        carousel-settings='{
          "draggable": true,
          "freeScroll": false,
          "wrapAround": false,
          "groupCells": true,
          "fade": false,
          "adaptiveHeight": false,
          "accessibility": true,
          "cellAlign": "left",
          "prevNextButtons": true,
          "pageDots": false
        }'
      >
        {gift.giftProducts.map((product, index) => {
          let discountedPrice = product.price * (100 - gift.discountValue) / 100
          const convertedDiscountValue = (gift.convertDiscountValue ? gift.discountValue * window.Shopify.currency.rate : gift.discountValue) * 100
          switch (gift.discountType) {
            case 'amount':
              discountedPrice = product.price - convertedDiscountValue
              break
            case 'fixed':
              discountedPrice = convertedDiscountValue
              break
          }
          const selectableOptions = []
          if (product.variants.length > 1) {
            product.options.forEach((option, index) => {
              const optionValues = new Set(product.variants.map(variant => variant[`option${index + 1}`]))
              if (optionValues.size > 1) {
                selectableOptions.push({ name: option, number: index + 1 })
              }
            })
          }
          const selectLabel = selectableOptions.length === 1 ? `Select ${selectableOptions[0].name}` : 'Select Variant'
          return (
            <div key={product.id} className={`w-full flex flex-row items-center gap-x-0.75 md:gap-x-1 transition-all ${index > 0 ? 'ml-1 md:ml-1.5' : ''}`}>
              <MiniCartItemImage imageSrc={product.featured_image} id={product.id} productPath={product.url} />
              <div className="flex-grow flex flex-col justify-between gap-y-1 size-full">
                <div className="flex flex-row justify-between gap-x-0.5 md:gap-x-1">
                  <div className="flex flex-col">
                    <h3 className="paragraph-s font-bold">
                      {product.title}
                    </h3>
                  </div>
                  <div className="flex flex-row gap-x-0.5 text-right">
                    <span className="paragraph-s line-through">{formatMoney(product.price)}</span>
                    <span className="paragraph-s text-brand-sale">{discountedPrice === 0 ? 'FREE' : formatMoney(discountedPrice)}</span>
                  </div>
                </div>
                <form action={globalState.routes.cart_add_url} method="post" onSubmit={onGwpAddToCart} className="flex flex-wrap items-center gap-0.5">
                  {product.variants.length > 1 ? (
                    <select name="id" className="h-[36px] paragraph-s leading-none">
                      <option value="" disabled selected>{selectLabel}</option>
                      {product.variants.map((variant) => {
                        const optionLabel = selectableOptions.length === 1 ? variant[`option${selectableOptions[0].number}`] : variant.title
                        return <option value={variant.id} key={variant.id} disabled={!variant.available}>{optionLabel}</option>
                      })}
                    </select>
                  ) : (
                    <input type="hidden" name="id" value={product.variants.find(variant => variant.available)?.id} />
                  )}
                  <input type="hidden" name="quantity" value={1} />
                  <input type="hidden" name="properties[_bl_gwp]" value={gift.id.split('/').pop()} />
                  <button type="submit" className="btn btn-primary btn-s">Add to Cart</button>
                </form>
              </div>
            </div>
          )
        })}
      </x-carousel>
    </div>
  )
}

const MiniCartProgressBar = ({ style, percentage, gifts }) => {
  switch (style) {
    case 'icons-on-bar':
      return <MiniCartIconsOnProgressBar percentage={percentage} gifts={gifts} />
    case 'gifts-on-bar':
      return <MiniCartGiftsOnProgressBar percentage={percentage} gifts={gifts} />
    default:
      return <MiniCartDefaultProgressBar percentage={percentage} gifts={gifts} />
  }
}

const MiniCartDefaultProgressBar = ({ percentage, gifts }) => {
  return (
    <div className="px-1 md:px-1.5 py-1.5 border-b border-borders-subdued">
      <div className="relative w-full h-0.5 bg-page-tertiary rounded-[4px]">
        <div
          className="absolute inset-tl w-gwp-bar-width h-full bg-type-black-primary transition-all rounded-[4px]"
          style={{ '--gwp-bar-width': `${percentage}%` }}
        ></div>
      </div>
      <div className={`pt-0.5 flex gap-x-1 ${gifts.length > 2 ? 'flex-col' : 'flex-row flex-wrap justify-between'}`}>
        {gifts.map((gift) => {
          return (
            <div className={`flex items-center gap-x-0.25 paragraph-xs ${gift.cartQualifies ? 'text-brand-black' : 'text-type-black-secondary'}`} key={gift.id}>
              <Icon id={gift.giftType === 'shipping' ? 'truck' : 'gift'} className="w-0.75 h-0.75" />
              {gift.message}
            </div>
          )
        })}
      </div>
    </div>
  )
}

const MiniCartIconsOnProgressBar = ({ percentage, gifts }) => {
  return (
    <div className="px-1 md:px-1.5 py-1.5 border-b border-borders-subdued">
      <div className="relative w-full h-0.5 bg-page-tertiary rounded-[4px]">
        <div
          className="absolute inset-tl w-gwp-bar-width h-full bg-type-black-primary transition-all rounded-[4px]"
          style={{ '--gwp-bar-width': `${percentage}%` }}
        ></div>
        {gifts.map((gift, index) => {
          return (
            <div className={`absolute top-1/2 w-1.25 h-1.25 p-0.25 border rounded-full transition-all ${gift.cartQualifies ? 'text-brand-white bg-brand-black border-brand-white' : 'text-brand-black bg-brand-white border-borders-default'}`} style={{
              left: `${(index + 1) / gifts.length * 100}%`,
              transform: `translate(-${(index + 1) / gifts.length * 80 + 10}%, -50%)`
            }}>
              <Icon id={gift.giftType === 'shipping' ? 'truck' : 'gift'} className="w-full h-full" />
              <span className="absolute top-full left-1/2 -translate-x-1/2 paragraph-xs text-brand-black">
                {gift.minPurchaseType === 'amount' ? formatMoney(gift.minPurchaseAmount).replace('.00', '') : gift.minPurchaseQuantity}
              </span>
            </div>
          )
        })}
      </div>
      <div className="pt-1.5">
        {gifts.map((gift) => {
          return (
            <div className={`flex items-center gap-x-0.25 paragraph-xs ${gift.cartQualifies ? 'text-brand-black' : 'text-type-black-secondary'}`} key={gift.id}>
              {gift.message}
            </div>
          )
        })}
      </div>
    </div>
  )
}

const MiniCartGiftsOnProgressBar = ({ percentage, gifts }) => {
  return (
    <div className="px-1 md:px-1.5 py-1.5 border-b border-borders-subdued">
      <div className="relative flex w-full bg-page-tertiary rounded-[4px]">
        <div
          className="absolute inset-tl w-gwp-bar-width h-full bg-type-black-primary transition-all rounded-[4px]"
          style={{ '--gwp-bar-width': `${percentage}%` }}
        ></div>
        {gifts.map((gift, index) => {
          return (
            <>
              {index > 0 && <div className="relative w-[2px] bg-brand-white"></div>}
              <div className={`relative flex items-center gap-x-0.25 px-0.5 py-0.25 rounded-[4px] paragraph-xs leading-none text-brand-white mix-blend-difference ${index === 0 ? 'flex-grow' : ''}`}>
                <Icon id={gift.giftType === 'shipping' ? 'truck' : 'gift'} className="w-0.75 h-0.75" />
                {gift.giftName}
              </div>
            </>
          )
        })}
      </div>
      <div className="pt-0.5">
        {gifts.map((gift) => {
          return (
            <div className={`flex items-center gap-x-0.25 paragraph-xs ${gift.cartQualifies ? 'text-brand-black' : 'text-type-black-secondary'}`} key={gift.id}>
              {gift.message}
            </div>
          )
        })}
      </div>
    </div>
  )
}

/**
 *
 * Display line item price
 * Handles different price if discount is applied
 *
 * Props Cart Line Item
 *
 * @param {*} param0
 * @returns
 */
const ItemPrice = ({ item }) => {
  const SalePrice = ({ salePrice, originalPrice }) => {
    return (
      <div className="flex flex-col items-end text-right">
        <p className="paragraph-xs text-type-black-secondary line-through">{formatMoney(originalPrice)}</p>
        <p className="paragraph-xs">{formatMoney(salePrice)}</p>
      </div>
    )
  }

  // Priority order: Discounts -> Compare At -> Regular (matches cart-line-item.liquid)
  if (item.discounts.length > 0 && item.original_line_price !== item.line_price) {
    return <SalePrice salePrice={item.line_price} originalPrice={item.original_line_price} />
  } else if (item.compare_at_price > item.original_price) {
    return <SalePrice salePrice={item.line_price} originalPrice={item.compare_at_price * item.quantity} />
  }

  return (
    <div className="flex flex-col items-end text-right">
      <p className="paragraph-xs">{formatMoney(item.line_price)}</p>
    </div>
  )
}

const MiniCartItemDiscount = ({ item }) => {
  if (!item.discounts?.length) return null
  return (
    <ul className="mt-0.5 list-none">
      {item.discounts.map((discount, index) => (
        <li className="flex" key={`${discount.title}-${index}`}>
          <span className="paragraph-xs font-base-spaced uppercase text-type-black-secondary">{discount.title}</span>
        </li>
      ))}
    </ul>
  )
}

/**
 *
 * Displays line item image
 *
 * @param {*} imageSrc
 * @param {*} id
 * @param {*} productPath
 * @returns
 */
const MiniCartItemImage = ({ imageSrc, id, productPath, ariaLabel }) => {
  return (
    <a href={productPath} className="shrink-0 link-fade" aria-label={ariaLabel || 'Item image'}>
      <PreactImage key={id} imageUrl={[imageSrc]} className="flex w-[94px] md:w-[110px] aspect-2/3 children:size-full children:object-cover" alt={ariaLabel || ''} />
    </a>
  )
}

/**
 *
 * Display the line item
 * 1) Title
 * 2) Variant Details (size, colour)
 * 3) Discount details (if applicable)
 * 4) Line Item price
 *
 * @param {*} param0
 * @returns
 */
const MiniCartItemDetails = ({ item }) => {
  const properties = item.properties
  const hasProperties = properties && typeof properties === 'object' && Object.keys(properties).length > 0

  const optionParts = (item.options_with_values ?? []).map(
    (option) => `${option.name}: ${option.value}`
  )

  const propertyEntries = hasProperties && properties
    ? Object.entries(properties).filter(([key, val]) => val != null && String(val) !== '' && key && !key.startsWith('_'))
    : []

  const title = getLineItemDisplayName(item)

  const criteria = useMemo(() => ({
    tags: item.tags ?? [],
    collections: item.collections?.map(c => c.handle) ?? [],
    products: [item.handle],
    location: 'Minicart'
  }), [item.tags, item.collections, item.handle])

  const allPromolines = usePromolines(criteria)

  const getObjects = (items) => {
    const exclusiveItem = items.find(item => item.exclusive)
    if (exclusiveItem) {
      return [exclusiveItem, null]
    }
    return [items[0] || null, items[1] || null]
  }

  const [firstPromoline, secondPromoline] = useMemo(() => getObjects(allPromolines), [allPromolines])

  return (
    <div className="flex flex-row justify-between gap-x-0.5 md:gap-x-1">
      <div className="flex flex-col">
        <p className="paragraph-xs font-base-spaced uppercase link-fade">
          {title}
        </p>

        {optionParts.length > 0 && (
          <p className="paragraph-xs text-type-black-secondary">
            {optionParts.join(' | ')}
          </p>
        )}

        {propertyEntries.length > 0 && (
          <ul className="list-none">
            {propertyEntries.map(([key, val]) => (
              <li key={key} className="list-none">
                <span className="paragraph-xs text-type-black-secondary">{key}: {String(val)}</span>
              </li>
            ))}
          </ul>
        )}

        {(firstPromoline || secondPromoline) && (
          <div className="flex flex-col gap-0.25">
            {firstPromoline && <Promoline promoline={firstPromoline} promolineLocation="Minicart" promolineSize="small" />}
            {secondPromoline && <Promoline promoline={secondPromoline} promolineLocation="Minicart" promolineSize="small" />}
          </div>
        )}

        <MiniCartItemDiscount item={item} />
      </div>
      <ItemPrice item={item} />
    </div>
  )
}

const MiniCartLineItem = ({ item, gift, handleSetCartConfig, isLoading }) => {
  const [itemLoading, setItemLoading] = useState(false)
  const loadingClass = itemLoading ? 'opacity-50 pointer-events-none' : ''

  const title = getLineItemDisplayName(item)

  return (
    <div className={`flex flex-col border-b border-borders-default transition-all ${loadingClass}`}>
      <div className="flex items-stretch">
        <MiniCartItemImage
          imageSrc={item.featured_image.url}
          id={item.id}
          productPath={item.url}
          ariaLabel={title}
        />
        <div className="grow flex flex-col gap-y-0.5 h-auto p-1 pb-0">
          <MiniCartItemDetails item={item} />
          {gift && <MiniCartGiftItem gift={gift} handleSetCartConfig={handleSetCartConfig} isLoading={isLoading} />}
          <MiniCartItemQuantity
            item={item}
            handleSetCartConfig={handleSetCartConfig}
            itemLoading={itemLoading}
            setItemLoading={setItemLoading}
          />
        </div>
      </div>
    </div>
  )
}

/**
 *
 * Displays cart total for Mini Cart.
 *
 * @param {*} param0
 * @returns
 */
const DisplayCartTotal = ({ total, isLoading, totalText }) => {
  const cartTotal = formatMoney(total)

  return (
    <div className="flex justify-between gap-x-1 px-margin py-1 border-b border-borders-default">
      <p className="paragraph-xs font-base-spaced font-semibold uppercase">{totalText || 'Total'}</p>
      <p className={`paragraph-xs font-base-spaced font-semibold text-right transition-all ${isLoading && 'opacity-50'}`}>{cartTotal}</p>
    </div>
  )
}

/**
 *
 * Empty Cart state. Renders when the customers cart is empty
 *
 * @param {*} param0
 * @returns
 */
const EmptyCart = ({ emptyState }) => {
  const emptyDescription = emptyState?.description
  const emptyLink = emptyState?.link?.url
  const emptyLinkText = emptyState?.link?.text

  return (
    <>
      <div className="grow flex flex-col justify-center gap-y-1 p-margin transition-all">
        {emptyDescription && <p className={'paragraph-xs'}>{emptyDescription}</p>}
        {emptyLinkText && (
          <a href={emptyLink} className={'btn btn-primary btn-border btn-m w-full'} aria-label={emptyLinkText}>{emptyLinkText}</a>
        )}
      </div>
    </>
  )
}

/**
 *
 * Cart Checkout button
 *
 * @param {*} param0
 * @returns
 */
const CheckoutButton = ({ checkoutButtonTitle }) => {
  return (
    <div className="px-margin pt-1">
      <a href="/checkout" className="btn btn-primary btn-fill btn-m lg:btn-l w-full" aria-label={checkoutButtonTitle}>
        {checkoutButtonTitle}
      </a>
    </div>
  )
}

/**
 *
 * Displays the line items present in the the customers cart.
 *
 * @param {*} param0
 * @returns
 */
const CartLineItems = ({ cart, giftsWithoutThreshold, handleSetCartConfig, isLoading }) => {
  const giftsDisplayed = []
  return (
    <div className="grow flex flex-col" data-role="cart_items">
      {cart.items.map((item) => {
        const giftToDisplay = giftsWithoutThreshold.find(gift => {
          if (!gift.cartQualifies || gift.giftInCart || giftsDisplayed.includes(gift.id)) {
            return false
          }
          switch (gift.conditionalType) {
            case 'all':
              return true
            case 'products':
              return gift.conditionalIds.some(id => id.split('/').pop() === item.product_id.toString())
            case 'collections':
              return item.collections?.some(collection => gift.conditionalIds.some(id => id.split('/').pop() === collection.id.toString()))
            case 'tags':
              return item.tags?.some(tag => gift.conditionalTags.includes(tag))
            default:
              return false
          }
        })
        if (giftToDisplay) {
          giftsDisplayed.push(giftToDisplay.id)
        }
        return (
          <MiniCartLineItem
            key={item.key}
            item={{ ...item }}
            gift={giftToDisplay}
            handleSetCartConfig={handleSetCartConfig}
            isLoading={isLoading}
          />
        )
      })}
    </div>
  )
}

/**
 *
 * Mini Cart
 *
 * @returns
 */
export function MiniCart() {
  const { cartConfig, handleSetCartConfig } = useCartGlobal()
  const globalCart = globalState.cart
  const { isLoading, cart } = cartConfig
  const freeShippingMinimum = globalCart?.freeShippingMinimum
  const {
    giftsWithoutThreshold,
    giftsForProgressBar,
    giftsProgressPercentage,
    nextAvailableGiftWithThreshold,
    invalidGiftItems
  } = useGwp(cart, freeShippingMinimum)
  const [removedGifts, setRemovedGifts] = useState([])
  const [removingGifts, setRemovingGifts] = useState(false)

  useLayoutEffect(() => {
    document.dispatchEvent(
      new CustomEvent('minicart:cart-rendered', {
        detail: { itemCount: cart?.item_count ?? 0 }
      })
    )
  }, [cart?.item_count])

  useEffect(() => {
    if (invalidGiftItems.length > 0 && !removingGifts) {
      setRemovingGifts(true)
      const removeItems = async () => {
        try {
          /** Set MiniCart state to isLoading true */
          handleSetCartConfig(CartConfigKeys.isLoading, true)
          /** Remove invalid gifts from cart */
          await window.Cart.updateLineItems(
            invalidGiftItems.reduce((acc, item) => ({
              ...acc,
              [item.key]: 0
            }), {}),
            { silent: true }
          )
          setRemovedGifts(invalidGiftItems)
          /** Poll Shopify for new Cart item and set cart config state */
          await window.Cart.getCart()
          handleSetCartConfig(CartConfigKeys.cart, window.Cart.value)
          handleSetCartConfig(CartConfigKeys.isLoading, false)
        } catch (error) {
          globalEmitter.emit(emitterProps.notice.global, {
            type: 'error',
            content: error.message
          })
          handleSetCartConfig(CartConfigKeys.isLoading, false)
        } finally {
          setRemovingGifts(false)
        }
      }
      removeItems()
    }
  }, [invalidGiftItems])

  const { checkoutButtonTitle, totalText, empty: emptyState } = globalCart

  useEffect(() => {
    const contentEl = document.getElementById('minicart-content')
    if (contentEl) {
      contentEl.classList.toggle('opacity-50', isLoading)
      contentEl.classList.toggle('pointer-events-none', isLoading)
    }
  }, [isLoading])

  const hasCartItems = cart?.items?.length > 0

  useEffect(() => {
    const summaryWrapper = document.getElementById('minicart-summary')
    const checkoutButtons = document.querySelectorAll(
      'div.additional-checkout-buttons--vertical, div.additional-checkout-buttons--horizontal'
    )

    if (summaryWrapper) {
      summaryWrapper.classList.toggle('hidden', !hasCartItems)
    }

    checkoutButtons.forEach((buttonGroup) => {
      buttonGroup.classList.toggle('hidden', !hasCartItems)
    })
  }, [hasCartItems])

  useEffect(() => {
    const handleRenderMiniCart = () => {
      if (!window.Cart) return
      handleSetCartConfig(CartConfigKeys.isLoading, true)
      window.Cart.getCart().then(() => {
        handleSetCartConfig(CartConfigKeys.cart, window.Cart.value)
        handleSetCartConfig(CartConfigKeys.isLoading, false)
      })
    }

    document.addEventListener('renderMiniCart', handleRenderMiniCart)
    return () => document.removeEventListener('renderMiniCart', handleRenderMiniCart)
  }, [])

  return (
    <>
      {cart?.items?.length ? (
        <>
          <MiniCartPortal targetId="minicart-shipping">
            <MiniCartSnippet cartSubTotal={cart.items_subtotal_price} />
          </MiniCartPortal>
          <MiniCartPortal targetId="minicart-gwp">
            {globalCart.gwp.enableProgressBar && giftsForProgressBar.length > 0 &&
              <MiniCartProgressBar style={globalCart.gwp.progressBarStyle} percentage={giftsProgressPercentage} gifts={giftsForProgressBar} />}
            {removedGifts.length > 0 && (
              <div className="flex items-center justify-between gap-x-0.5 bg-type-white-secondary px-1 md:px-1.5 py-1.5 text-xs">
                {`Sorry, you no longer qualify for ${removedGifts.length > 1 ? 'some free gifts and they have been removed' : 'a free gift and it has been removed'}.`}
                <button className="btn btn-primary btn-text btn-xs" onClick={() => setRemovedGifts([])}>
                  <Icon id="close" className="w-[10px] h-[10px]" />
                </button>
              </div>
            )}
            {nextAvailableGiftWithThreshold && <MiniCartGiftItem gift={nextAvailableGiftWithThreshold} handleSetCartConfig={handleSetCartConfig} isLoading={isLoading} />}
          </MiniCartPortal>
          <MiniCartPortal targetId="minicart-items">
            <CartLineItems cart={cart} giftsWithoutThreshold={giftsWithoutThreshold} isLoading={isLoading} handleSetCartConfig={handleSetCartConfig} />
          </MiniCartPortal>
          <MiniCartPortal targetId="minicart-summary-mount">
            <div className="flex flex-col">
              <DisplayCartTotal
                totalCartDiscount={cart.total_discount}
                total={cart.total_price}
                isLoading={isLoading}
                cart={cart}
                totalText={totalText}
              />
              <CheckoutButton checkoutButtonTitle={checkoutButtonTitle} />
            </div>
          </MiniCartPortal>
        </>
      ) : (
        <MiniCartPortal targetId="minicart-items">
          <EmptyCart emptyState={emptyState} />
        </MiniCartPortal>
      )}
    </>
  )
}

const MiniCartPortal = ({ children, targetId }) => {
  const target = document.getElementById(targetId)
  return target ? createPortal(children, target) : null
}
